import { motion } from 'framer-motion';
import { ArrowRight, BookOpen, Calendar, Moon, Star } from 'lucide-react';
import { BackButton } from './ui/BackButton';

interface RamadanIntroPageProps {
  onNext: () => void;
  onBack: () => void;
}

export const RamadanIntroPage = ({ onNext, onBack }: RamadanIntroPageProps) => {
  return (
    <div className="min-h-screen bg-paper">
      <header className="border-b border-border/50">
        <div className="max-w-3xl mx-auto px-6 py-4 flex justify-between items-center">
          <BackButton onClick={onBack} label="Back" srLabel="Return to introduction" />
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
            The Journey Ahead
          </span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="space-y-12"
        >
          {/* Header */}
          <div className="text-center space-y-4">
            <div className="text-4xl" style={{ color: 'hsl(38, 40%, 55%)' }}>☽</div>
            <h1 className="font-display text-3xl md:text-4xl text-ink">
              The Blessed Month
            </h1>
            <p className="text-muted-foreground max-w-md mx-auto">
              A time unlike any other in the Islamic calendar
            </p>
          </div>

          {/* Key Points */}
          <div className="grid gap-6">
            <motion.div 
              className="bg-cream rounded-lg p-6 space-y-3"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="flex items-center gap-3">
                <BookOpen className="w-5 h-5 text-gold" />
                <h3 className="font-display text-lg text-ink">The Month of Qur'an</h3>
              </div>
              <p className="text-foreground/80 text-sm leading-relaxed">
                Ramadan is when the Qur'an was first revealed. The Prophet ﷺ would review the entire 
                Qur'an with Jibreel every Ramadan. This is the month to reconnect with Allah's words—
                whether through recitation, reflection, or both.
              </p>
            </motion.div>

            <motion.div 
              className="bg-cream rounded-lg p-6 space-y-3"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="flex items-center gap-3">
                <Star className="w-5 h-5 text-gold" />
                <h3 className="font-display text-lg text-ink">The Month of Du'a</h3>
              </div>
              <p className="text-foreground/80 text-sm leading-relaxed">
                "When My servants ask you about Me, I am near. I respond to the call of the caller 
                when they call upon Me." (2:186) This verse appears in the middle of fasting verses—
                a reminder that Ramadan is prime time for your prayers to be answered.
              </p>
            </motion.div>

            <motion.div 
              className="bg-cream rounded-lg p-6 space-y-3"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
            >
              <div className="flex items-center gap-3">
                <Moon className="w-5 h-5 text-gold" />
                <h3 className="font-display text-lg text-ink">Laylatul Qadr</h3>
              </div>
              <p className="text-foreground/80 text-sm leading-relaxed">
                Hidden within the last ten nights is a night better than 1,000 months. The angels 
                descend, decrees are written, and worship is multiplied beyond measure. One sincere 
                night could change the trajectory of your life.
              </p>
            </motion.div>
          </div>

          {/* What this journey helps you do */}
          <div className="space-y-4">
            <h2 className="font-display text-xl text-ink text-center">
              What This Journey Helps You Do
            </h2>
            <ul className="space-y-3 text-foreground/80">
              <li className="flex items-start gap-3">
                <span className="text-gold mt-0.5">•</span>
                <span>Build a daily practice of meaningful du'a and reflection</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-gold mt-0.5">•</span>
                <span>Track your worship without feeling overwhelmed</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-gold mt-0.5">•</span>
                <span>Connect with Allah through His beautiful names</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-gold mt-0.5">•</span>
                <span>Journal your thoughts, struggles, and growth</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-gold mt-0.5">•</span>
                <span>Prepare for and make the most of Laylatul Qadr</span>
              </li>
            </ul>
          </div>

          {/* How it works */}
          <div className="bg-cream rounded-lg p-6 space-y-4">
            <div className="flex items-center gap-3 justify-center">
              <Calendar className="w-5 h-5 text-gold" />
              <h2 className="font-display text-xl text-ink">How It Works</h2>
            </div>
            <div className="grid gap-4 text-sm">
              <div className="flex items-start gap-4">
                <span className="text-gold font-medium">01</span>
                <p className="text-foreground/80">
                  Set your Ramadan start date and we'll calculate each day for you
                </p>
              </div>
              <div className="flex items-start gap-4">
                <span className="text-gold font-medium">02</span>
                <p className="text-foreground/80">
                  Each day, read the dua, reflect on the theme, and update your trackers
                </p>
              </div>
              <div className="flex items-start gap-4">
                <span className="text-gold font-medium">03</span>
                <p className="text-foreground/80">
                  Journal your thoughts—how you're feeling, what you need from Allah
                </p>
              </div>
              <div className="flex items-start gap-4">
                <span className="text-gold font-medium">04</span>
                <p className="text-foreground/80">
                  Your progress syncs across devices (if signed in) so you never lose track
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="flex flex-col items-center gap-4 pt-4">
            <button
              onClick={onNext}
              className="group flex items-center gap-3 px-8 py-3 bg-ink text-warm-white rounded-sm
                         hover:bg-espresso transition-colors text-sm uppercase tracking-[0.15em]"
            >
              Continue
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </motion.div>
      </main>
    </div>
  );
};
